// src/components/Workstation.jsx
// Fixes:
//   1. formatNaira imported from useSubscription (single source of truth)
//   2. Subscription tier shown so stylists know their standing
//   3. All original asset tabs, purchased assets display preserved exactly
//   4. No Paystack anywhere — store navigation handles purchase

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useSubscription, formatNaira } from '../hooks/useSubscription';

const GOLD = '#B8860B';

export default function Workstation() {
  const navigate = useNavigate();
  const sub      = useSubscription();

  const [activeTab,    setActiveTab]    = useState('basic');
  const [stylistData,  setStylistData]  = useState(null);
  const [allAssets,    setAllAssets]    = useState([]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubStylist = onSnapshot(doc(db, 'barbers', user.uid), (snap) => {
      if (snap.exists()) setStylistData(snap.data());
    });

    const unsubAssets = onSnapshot(collection(db, 'globalAssets'), (snap) => {
      setAllAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubStylist(); unsubAssets(); };
  }, []);

  const ownedAssetIds = stylistData?.purchasedAssets || [];
  const basicAssets   = allAssets.filter(a => a.isFree || a.price === 0);
  const premiumAssets = allAssets.filter(a => ownedAssetIds.includes(a.id));
  const lockedAssets  = allAssets.filter(a => !a.isFree && a.price > 0 && !ownedAssetIds.includes(a.id));

  return (
    <div style={panelContainer}>
      <h3 style={panelTitle}>HAIR TEXTURE ARCHIVE</h3>

      {/* Subscription indicator */}
      {!sub.loading && (
        <div style={subRow}>
          {sub.isSuperAdmin
            ? <span style={activePill}>⚡ ROOT ACCESS</span>
            : sub.isSubscriptionActive
              ? <span style={activePill}>👑 {sub.tier?.toUpperCase()} SHIELD · {sub.monthlyScansUsed}/{sub.PRICING[sub.tier]?.scansPerMonth} scans</span>
              : <span style={freePill}>FREE · <span onClick={() => navigate('/subscription')} style={{ color: GOLD, cursor: 'pointer' }}>Upgrade →</span></span>
          }
        </div>
      )}

      <div style={tabBar}>
        <button
          onClick={() => setActiveTab('basic')}
          style={activeTab === 'basic' ? activeTabStyle : inactiveTabStyle}
        >
          BASIC
        </button>
        <button
          onClick={() => setActiveTab('premium')}
          style={activeTab === 'premium' ? activePremiumTabStyle : inactiveTabStyle}
        >
          OWNED ({premiumAssets.length})
        </button>
        <button
          onClick={() => setActiveTab('store')}
          style={activeTab === 'store' ? activePremiumTabStyle : inactiveTabStyle}
        >
          STORE ({lockedAssets.length})
        </button>
      </div>

      <div style={textureGrid}>
        {activeTab === 'basic' && (
          basicAssets.length > 0
            ? basicAssets.map(asset => <TextureItem key={asset.id} asset={asset} />)
            : <p style={emptyMsg}>No basic assets yet.</p>
        )}

        {activeTab === 'premium' && (
          premiumAssets.length > 0
            ? premiumAssets.map(asset => <TextureItem key={asset.id} asset={asset} isPremium />)
            : (
              <div style={emptyState}>
                <p style={{ fontSize: '0.7rem', opacity: 0.6 }}>No premium assets owned yet.</p>
                <button onClick={() => navigate('/store')} style={storeBtn}>ENTER STORE</button>
              </div>
            )
        )}

        {activeTab === 'store' && (
          lockedAssets.length > 0
            ? lockedAssets.map(asset => (
              <div key={asset.id} style={{ ...itemCard, borderColor: 'rgba(255,255,255,0.1)', position: 'relative', cursor: 'pointer' }}
                   onClick={() => navigate('/store')}>
                <img src={asset.image || asset.previewUrl || asset.fileUrl} style={previewImg} alt="Hair" />
                <div style={lockOverlay}>
                  <span style={{ fontSize: '1rem' }}>🔒</span>
                  <span style={{ fontSize: '0.5rem', color: GOLD, fontWeight: '900' }}>
                    {formatNaira(asset.price)}
                  </span>
                </div>
              </div>
            ))
            : <p style={emptyMsg}>All available assets are owned.</p>
        )}
      </div>

      {activeTab !== 'store' && lockedAssets.length > 0 && (
        <button onClick={() => navigate('/store')} style={{ ...storeBtn, marginTop: '12px', width: '100%' }}>
          UNLOCK MORE ASSETS IN STORE
        </button>
      )}
    </div>
  );
}

const TextureItem = ({ asset, isPremium }) => (
  <div style={{ ...itemCard, borderColor: isPremium ? GOLD : 'rgba(255,255,255,0.1)' }}>
    <img
      src={asset.image || asset.previewUrl || asset.fileUrl}
      style={previewImg}
      alt="Hair"
    />
    {isPremium && <span style={premiumBadge}>OWNED</span>}
  </div>
);

// ── Styles ────────────────────────────────────────────────────────────────────
const panelContainer     = { background:'#001F3F', padding:'15px', borderRadius:'15px', border:`1px solid ${GOLD}44`, fontFamily:'Montserrat, sans-serif' };
const panelTitle         = { fontSize:'0.7rem', color:GOLD, marginBottom:'10px', letterSpacing:'2px', textAlign:'center', fontWeight:'bold' };
const subRow             = { textAlign:'center', marginBottom:'12px', fontSize:'0.62rem' };
const activePill         = { color:GOLD, fontWeight:'700' };
const freePill           = { color:'rgba(255,255,255,0.5)' };
const tabBar             = { display:'flex', gap:'5px', marginBottom:'15px', background:'rgba(0,0,0,0.3)', padding:'5px', borderRadius:'10px' };
const activeTabStyle     = { flex:1, background:'#fff', color:'#000', border:'none', padding:'8px', borderRadius:'7px', fontWeight:'bold', fontSize:'0.65rem', cursor:'pointer' };
const activePremiumTabStyle = { flex:1, background:GOLD, color:'#000', border:'none', padding:'8px', borderRadius:'7px', fontWeight:'bold', fontSize:'0.65rem', cursor:'pointer' };
const inactiveTabStyle   = { flex:1, background:'transparent', color:'#fff', border:'none', padding:'8px', opacity:0.5, fontSize:'0.65rem', cursor:'pointer' };
const textureGrid        = { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', maxHeight:'280px', overflowY:'auto' };
const itemCard           = { position:'relative', borderRadius:'10px', overflow:'hidden', border:'2px solid' };
const previewImg         = { width:'100%', height:'65px', objectFit:'cover', display:'block' };
const premiumBadge       = { position:'absolute', bottom:0, left:0, right:0, background:GOLD, color:'black', fontSize:'0.5rem', fontWeight:'900', textAlign:'center', padding:'2px 0' };
const lockOverlay        = { position:'absolute', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'4px' };
const emptyState         = { gridColumn:'1/-1', textAlign:'center', padding:'20px' };
const emptyMsg           = { gridColumn:'1/-1', textAlign:'center', opacity:0.4, fontSize:'0.7rem', padding:'20px 0' };
const storeBtn           = { background:GOLD, border:'none', padding:'8px 15px', borderRadius:'8px', color:'black', fontWeight:'900', marginTop:'10px', cursor:'pointer', fontSize:'0.7rem' };
